import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowDown, Mountain } from "lucide-react";
import { useI18n } from "@/i18n/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];

function AltitudeCounter({ label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const [val, setVal] = useState(0);
  const { lang } = useI18n();

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, 1850, {
      duration: 2.4,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView]);

  return (
    <div ref={ref} data-testid="altitude-counter" className="text-right">
      <div className="font-mono text-4xl font-light text-copper sm:text-6xl">
        {val.toLocaleString(lang === "en" ? "en-US" : "fr-FR")}
        <span className="text-xl sm:text-3xl">m</span>
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-sand sm:text-xs">
        {label}
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { t, lang } = useI18n();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-4%", "6%"]);

  const LINES = [
    <>{t.hero.line1}</>,
    <><span className="italic font-medium text-copper">{t.hero.line2Em}</span> {t.hero.line2Post}</>,
    <>{t.hero.line3}<span className="text-copper">.</span></>,
  ];

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-cream">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-10 left-0 select-none font-serif text-[22vw] leading-none text-pine/[0.04]"
      >
        MONTE YOLLI
      </div>

      <div className="mx-auto grid max-w-7xl gap-14 px-4 pb-16 pt-32 sm:px-8 lg:grid-cols-2 lg:items-center lg:pb-24 lg:pt-40">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            data-testid="hero-altitude-badge"
            className="mb-8 inline-flex w-fit items-center gap-2 border border-copper/30 bg-paper px-4 py-2"
          >
            <Mountain className="h-3.5 w-3.5 text-copper" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-copper sm:text-xs">
              {t.hero.badge}
            </span>
          </motion.div>

          <h1 key={lang} className="font-serif text-4xl leading-[1.05] tracking-tight text-pine sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.16, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-sand sm:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#varietes"
              data-testid="hero-cta-varieties"
              className="group inline-flex items-center gap-3 bg-copper px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-pine"
            >
              {t.hero.ctaVarieties}
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-b2b"
              className="border border-pine/30 px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-pine transition-all duration-300 hover:border-copper hover:text-copper"
            >
              {t.hero.ctaB2b}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
          className="relative"
        >
          <div
            className="bg-gradient-to-br from-copper/60 via-copper/15 to-pine/30 p-px"
            style={{ clipPath: "polygon(0 0, 92% 0, 100% 8%, 100% 100%, 0 100%)" }}
          >
            <div
              className="relative overflow-hidden"
              style={{ clipPath: "polygon(0 0, 92% 0, 100% 8%, 100% 100%, 0 100%)" }}
            >
              <motion.img
                style={{ y: imgY }}
                src="/assets/hero-theme.png"
                alt="Monte Yolli — Café de la Sierra"
                className="h-[420px] w-full scale-105 object-cover sm:h-[540px]"
              />
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
            className="absolute -bottom-5 left-6 border border-copper/30 bg-paper px-4 py-2 shadow-lg shadow-pine/10"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-copper">
              {t.hero.chip}
            </span>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="mx-auto flex w-full max-w-7xl items-end justify-between px-4 pb-10 sm:px-8"
      >
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
          <span className="h-px w-10 bg-copper/50" />
          {t.hero.location}
        </div>
        <AltitudeCounter label={t.hero.altitudeLabel} />
      </motion.div>
    </section>
  );
}
